import { Banknote, Clock, HandCoins, Key, Store } from 'lucide-vue-next'
import ColumnLink from 'pejer/src/components/Table/core/ColumnLink.vue'
import { h } from 'vue'
import { Groups, JSSchema } from '.'
import LimitedDisplay from '../components/Table/core/LimitedDisplay.vue'
import type { Environment } from '../types/types'
import { ConnectedAccountFullSchema, ConnectedAccounts } from './ConnectedAccounts'
import {
  Currency,
  CurrencyList,
  MoneyFormatter,
  PaymentEnvironmentList,
  PaymentStatusFormatter,
} from './Global'
import { ColorPalette, textWithIconCell } from './helpers'

export type PayoutReason = (typeof PayoutReasonList)[number]
export const PayoutReasonList = [
  'PayoutTriggeredFromAPI',
  'PayoutTriggeredFromPejerAdminPanel',
] as const

export namespace Payout {
  export interface Full {
    id: number
    payoutId: string
    platformID: string
    environment: Environment
    connectedAccount: ConnectedAccounts.Full
    payoutReason: PayoutReason
    fromBankAccountNumber?: string
    toBankAccountNumber?: string
    amount: number
    currency: Currency
    shortDescription?: string
    externalObjectIdTag?: string
    listOfLedgerIds?: number[]
    listOfLedgerIdsString?: string
    ledgerItems: ConnectedAccountLedgerDto[]
    payoutFee: number
    insertTime: string
  }
  export interface ListItem {
    id: number
    payoutId: string
    platformID: string
    environment: Environment
    connectedAccountID: string
    payoutReason: PayoutReason
    fromBankAccountNumber?: string
    toBankAccountNumber?: string
    amount: number
    currency: Currency
    shortDescription?: string
    externalObjectIdTag?: string
    payoutFee: number
    connectedAccount: {
      externalReferenceID: string
      name: string
      email: string
      payoutsEnabled: boolean
      businessName: string
      businessType: string
      business_TaxID: string
      countryCode: string
      bban: string
      iban: string
      bankName: string
      bic: string
      swift: string
      website: string
      business_Address: string
      business_City: string
      business_PostalCode: string
      business_Phone: string
      business_PhonePrefix: string
      individual_Title: string
      individual_Firstname: string
      individual_Lastname: string
      individual_Email: string
      individual_Phone: string
      individual_PhonePrefix: string
      individual_BirthDay: Date | null
      individual_City: string
      individual_CountryCode: string
      individual_Address: string
      individual_Zipcode: string
      purposeDescription: string
      tosIsAccepted: Date | null
      signature_Completed: Date | null
      zimplePayPartialIsAccepted: Date | null
      lastPayoutsEnabledCheck: Date | null
      lastPayoutAttempt: Date | null
      info: string
    }

    insertTime: string
  }
  type ConnectedAccountLedgerDto = {
    ledgerID: number
    splitID: number
    connectedAccountID: string | null
    platformID: string
    paymentReferenceID: string
    environment: 'production' | 'test'
    currency: 'NOK'
    amount: number
    description: string | null
    insertTime: string
    isSettledPayoutID: number | null
  }
}

export const PayoutGroups = {
  Account: {
    icon: Store,
    color: ColorPalette.pink,
    mainColumn: 'connectedAccount.name',
  },
  ID: {
    icon: Key,
    color: ColorPalette.blue,
    mainColumn: 'payoutId',
  },
  Payout: {
    icon: HandCoins,
    color: ColorPalette.green,
    isMainGroup: true,
    mainColumn: 'shortDescription',
  },
  Amount: {
    icon: Banknote,
    color: ColorPalette.green,
    mainColumn: 'amount',
  },
  Time: {
    icon: Clock,
    color: ColorPalette.black,
    mainColumn: 'insertTime',
  },
} satisfies Groups<Payout.ListItem>

// Using chatGPT to generate the schema. see index.ts...
export const PayoutListSchema: JSSchema<Payout.ListItem, typeof PayoutGroups> = {
  name: 'Payouts',
  title: 'Payouts',
  groups: PayoutGroups,
  fields: {
    id: {
      type: 'number',
      humanReadable: 'ID',
      meta: {
        group: 'ID',
      },
    },
    payoutId: {
      type: 'string',
      humanReadable: 'Payout ID',
      meta: {
        group: 'ID',
      },
      formattedCell: (value: string) => {
        return h(
          ColumnLink,
          {
            content: value,
            id: value,
            pathName: 'payout',
          },
          () => h(LimitedDisplay, { content: value, oneLine: true })
        )
      },
    },
    platformID: {
      type: 'string',
      humanReadable: 'Platform ID',
      meta: {
        group: 'ID',
      },
    },
    environment: {
      type: 'enum',
      humanReadable: 'Operational Environment',
      options: PaymentEnvironmentList,
      formatter: PaymentStatusFormatter,
      meta: {
        group: 'Payout',
      },
    },
    connectedAccountID: {
      type: 'string',
      humanReadable: 'Connected Account ID',
      meta: {
        group: 'Account',
      },
    },
    payoutReason: {
      type: 'enum',
      humanReadable: 'Reason for Payout',
      options: PayoutReasonList,
      meta: {
        group: 'Payout',
      },
      sensitive: true,
    },
    fromBankAccountNumber: {
      type: 'string',
      humanReadable: 'Source Bank Account Number',
      meta: {
        group: 'Payout',
      },
      sensitive: true,
    },
    toBankAccountNumber: {
      type: 'string',
      humanReadable: 'To Bank Account Number',
      meta: {
        group: 'Payout',
      },
      sensitive: true,
    },
    amount: {
      type: 'number',
      humanReadable: 'Payout Amount',
      formatter: MoneyFormatter,
      metaType: 'money',
      meta: {
        group: 'Amount',
      },
    },
    currency: {
      type: 'enum',
      humanReadable: 'Currency',
      options: CurrencyList,
      meta: {
        group: 'Payout',
      },
    },
    shortDescription: {
      type: 'string',
      humanReadable: 'Short Description',
      meta: {
        group: 'Payout',
      },
      sensitive: true,
    },
    externalObjectIdTag: {
      type: 'string',
      humanReadable: 'External Object ID Tag',
      meta: {
        group: 'ID',
      },
    },
    payoutFee: {
      type: 'number',
      humanReadable: 'Fee',
      formatter: MoneyFormatter,
      metaType: 'money',
      meta: {
        group: 'Amount',
      },
    },
    insertTime: {
      type: 'date',
      humanReadable: 'Created at',
      meta: {
        group: 'Time',
      },
      isDefaultSortColumn: true,
    },
    connectedAccount: {
      type: 'object',
      humanReadable: 'Connected Account Details',
      values: {
        externalReferenceID: {
          relation: 'connectedAccounts.externalReferenceID',
          meta: {
            group: 'Account',
          },
        },
        name: {
          relation: 'connectedAccounts.name',
          meta: {
            group: 'Account',
          },
          specificCell:
            () =>
            ({ row }) => {
              return h(
                ColumnLink,
                {
                  id: row.original.connectedAccountID,
                  pathName: 'account',
                  content: row.original.connectedAccount.name,
                },
                () => textWithIconCell(Store, row.original.connectedAccount.name)
              )
            },
        },
        email: {
          relation: 'connectedAccounts.email',
          meta: {
            group: 'Account',
          },
        },
        payoutsEnabled: {
          relation: 'connectedAccounts.payoutsEnabled',
          meta: {
            group: 'Account',
          },
        },
        businessName: {
          relation: 'connectedAccounts.businessName',
          humanReadable: 'Business Name',
          meta: {
            group: 'Account',
          },
        },
        businessType: {
          relation: 'connectedAccounts.businessType',
          meta: {
            group: 'Account',
          },
        },
        business_TaxID: {
          relation: 'connectedAccounts.business_TaxID',
          meta: {
            group: 'Account',
          },
        },
        bban: {
          relation: 'connectedAccounts.bban',
          meta: {
            group: 'Account',
          },
        },
        iban: {
          relation: 'connectedAccounts.iban',
          meta: {
            group: 'Account',
          },
        },
        bankName: {
          relation: 'connectedAccounts.bankName',
          meta: {
            group: 'Account',
          },
        },
        bic: {
          relation: 'connectedAccounts.bic',
          meta: {
            group: 'Account',
          },
        },
        swift: {
          relation: 'connectedAccounts.swift',
          meta: {
            group: 'Account',
          },
        },
        website: {
          relation: 'connectedAccounts.website',
          meta: {
            group: 'Account',
          },
        },
        countryCode: {
          relation: 'connectedAccounts.countryCode',
          meta: {
            group: 'Account',
          },
        },
        business_Address: {
          relation: 'connectedAccounts.business_Address',
          meta: {
            group: 'Account',
          },
        },
        business_City: {
          relation: 'connectedAccounts.business_City',
          meta: {
            group: 'Account',
          },
        },
        business_PostalCode: {
          relation: 'connectedAccounts.business_PostalCode',
          meta: {
            group: 'Account',
          },
        },
        business_Phone: {
          relation: 'connectedAccounts.business_Phone',
          meta: {
            group: 'Account',
          },
        },
        business_PhonePrefix: {
          relation: 'connectedAccounts.business_PhonePrefix',
          meta: {
            group: 'Account',
          },
        },
        individual_Title: {
          relation: 'connectedAccounts.individual_Title',
          meta: {
            group: 'Account',
          },
        },
        individual_Firstname: {
          relation: 'connectedAccounts.individual_Firstname',
          meta: {
            group: 'Account',
          },
        },
        individual_Lastname: {
          relation: 'connectedAccounts.individual_Lastname',
          meta: {
            group: 'Account',
          },
        },
        individual_Email: {
          relation: 'connectedAccounts.individual_Email',
          meta: {
            group: 'Account',
          },
        },
        individual_Phone: {
          relation: 'connectedAccounts.individual_Phone',
          meta: {
            group: 'Account',
          },
        },
        individual_PhonePrefix: {
          relation: 'connectedAccounts.individual_PhonePrefix',
          meta: {
            group: 'Account',
          },
        },
        individual_BirthDay: {
          relation: 'connectedAccounts.individual_BirthDay',
          meta: {
            group: 'Account',
          },
        },
        individual_City: {
          relation: 'connectedAccounts.individual_City',
          meta: {
            group: 'Account',
          },
        },
        individual_CountryCode: {
          relation: 'connectedAccounts.individual_CountryCode',
          meta: {
            group: 'Account',
          },
        },
        individual_Address: {
          relation: 'connectedAccounts.individual_Address',
          meta: {
            group: 'Account',
          },
        },
        individual_Zipcode: {
          relation: 'connectedAccounts.individual_Zipcode',
          meta: {
            group: 'Account',
          },
        },
        purposeDescription: {
          relation: 'connectedAccounts.purposeDescription',
          meta: {
            group: 'Account',
          },
        },
        tosIsAccepted: {
          relation: 'connectedAccounts.tosIsAccepted',
          meta: {
            group: 'Account',
          },
        },
        signature_Completed: {
          relation: 'connectedAccounts.signature_Completed',
          meta: {
            group: 'Account',
          },
        },
        zimplePayPartialIsAccepted: {
          relation: 'connectedAccounts.zimplePayPartialIsAccepted',
          meta: {
            group: 'Account',
          },
        },

        lastPayoutsEnabledCheck: {
          type: 'date',
          humanReadable: 'Last Payouts Enabled Check',
          meta: {
            group: 'Account',
          },
        },
        lastPayoutAttempt: {
          type: 'date',
          humanReadable: 'Last Payout Attempt',
          meta: {
            group: 'Account',
          },
        },
        info: {
          relation: 'connectedAccounts.info',
          meta: {
            group: 'Account',
          },
        },
      },
    },
  },
}

export const PayoutFullSchema: JSSchema<Payout.Full, typeof PayoutGroups> = {
  name: 'PayoutsFull',
  title: 'Payout',
  groups: PayoutGroups,
  fields: {
    id: {
      type: 'number',
      humanReadable: 'Unique ID',
    },
    payoutId: {
      type: 'string',
      humanReadable: 'Payout ID',
    },
    platformID: {
      type: 'string',
      humanReadable: 'Platform ID',
    },
    environment: {
      type: 'enum',
      humanReadable: 'Operational Environment',
      options: PaymentEnvironmentList,
    },
    // Don't really need the relation column since we are just using the full schema of the connected account
    connectedAccount: {
      type: 'object',
      values: ConnectedAccountFullSchema,
      humanReadable: 'Connected Account Details',
    },
    payoutReason: {
      type: 'enum',
      humanReadable: 'Reason for Payout',
      options: PayoutReasonList,
      sensitive: true,
    },
    fromBankAccountNumber: {
      type: 'string',
      humanReadable: 'Source Bank Account Number',
      optional: true,
      sensitive: true,
    },
    toBankAccountNumber: {
      type: 'string',
      humanReadable: 'Destination Bank Account Number',
      optional: true,
      sensitive: true,
    },
    amount: {
      type: 'number',
      humanReadable: 'Payout Amount',
      formatter: MoneyFormatter,
      metaType: 'money',
    },
    currency: {
      type: 'enum',
      humanReadable: 'Currency',
      options: CurrencyList,
    },
    shortDescription: {
      type: 'string',
      humanReadable: 'Short Description',
      optional: true,
      sensitive: true,
    },
    externalObjectIdTag: {
      type: 'string',
      humanReadable: 'External Object ID Tag',
      optional: true,
    },
    listOfLedgerIds: {
      type: 'array',
      humanReadable: 'List of Ledger IDs',
      optional: true,
    },
    listOfLedgerIdsString: {
      type: 'string',
      humanReadable: 'Comma-separated Ledger IDs',
      optional: true,
    },
    ledgerItems: {
      type: 'array',
      values: {
        ledgerID: { type: 'number', humanReadable: 'Ledger ID' },
        splitID: { type: 'number', humanReadable: 'Split ID' },
        connectedAccountID: {
          type: 'string',
          humanReadable: 'Connected Account ID',
          optional: true,
        },
        platformID: { type: 'string', humanReadable: 'Platform ID' },
        paymentReferenceID: { type: 'string', humanReadable: 'Payment Reference ID' },
        environment: {
          type: 'enum',
          options: PaymentEnvironmentList,
          humanReadable: 'Environment',
        },
        currency: { type: 'enum', options: CurrencyList, humanReadable: 'Currency' },
        amount: {
          type: 'number',
          humanReadable: 'Amount',
          formatter: MoneyFormatter,
          metaType: 'money',
        },
        description: {
          type: 'string',
          humanReadable: 'Description',
          optional: true,
          sensitive: true,
        },
        insertTime: { type: 'date', humanReadable: 'Insert Time' },
        isSettledPayoutID: {
          type: 'number',
          humanReadable: 'Is Settled Payout ID',
          optional: true,
        },
      },
      humanReadable: 'Ledger Items',
    },
    payoutFee: {
      type: 'number',
      humanReadable: 'Payout Fee',
      formatter: MoneyFormatter,
      metaType: 'money',
    },
    insertTime: {
      type: 'date',
      humanReadable: 'Insert Time',
      isDefaultSortColumn: true,
    },
  },
}
